import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import "./AccountLedgerReport.css";
import { MasterTableSkeleton } from "../Skeleton";

const getCurrentFYFirstDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const startYear = m >= 4 ? y : y - 1;
  return `${startYear}-04-01`;
};

const getCurrentDateString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatINR = (val) => {
  return Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function AccountLedgerReport() {
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || localStorage.getItem("companyId") || "4211";
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

  const [accounts, setAccounts] = useState([]);
  const [selectedHeadCode, setSelectedHeadCode] = useState("");
  const [fromDate, setFromDate] = useState(getCurrentFYFirstDate);
  const [toDate, setToDate] = useState(getCurrentDateString);

  const [statementData, setStatementData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch accounts list for dropdown
  useEffect(() => {
    if (!companyId) return;
    const fetchAccounts = async () => {
      try {
        const res = await axios.get(`${BASE_API_URL}/reports/ledger/accounts/${companyId}`);
        if (res.data?.success) {
          const accs = res.data.data || [];
          setAccounts(accs);
          if (accs.length > 0 && !selectedHeadCode) {
            setSelectedHeadCode(String(accs[0].HEAD_CODE));
          }
        }
      } catch (err) {
        console.error("Error loading accounts:", err);
      }
    };
    fetchAccounts();
  }, [BASE_API_URL, companyId]);

  // Fetch statement data
  const fetchStatement = useCallback(async () => {
    if (!companyId || !selectedHeadCode) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/reports/ledger/statement/${companyId}`, {
        params: {
          headCode: selectedHeadCode,
          fromDate,
          toDate,
        },
      });
      if (res.data?.success) {
        setStatementData(res.data.data);
      } else {
        setStatementData(null);
      }
    } catch (err) {
      console.error("Error fetching ledger statement:", err);
      showToast.error("Failed to load account statement");
    } finally {
      setIsLoading(false);
    }
  }, [BASE_API_URL, companyId, selectedHeadCode, fromDate, toDate]);

  useEffect(() => {
    if (selectedHeadCode) {
      fetchStatement();
    }
  }, [fetchStatement, selectedHeadCode]);

  const handleExportExcel = () => {
    if (!statementData || !statementData.transactions.length) {
      showToast.warn("No transactions to export");
      return;
    }

    const account = statementData.account;
    const rows = [
      [`Statement of Account: ${account.headName}`],
      [`Group: ${account.groupName} | Period: ${fromDate} to ${toDate}`],
      [`Opening Balance: ₹${formatINR(statementData.openingBalance.amount)} ${statementData.openingBalance.type}`],
      [],
      ["Date", "Voucher No", "Type", "Ref / Inv #", "Particulars", "Debit (₹)", "Credit (₹)", "Balance (₹)", "Dr/Cr"],
    ];

    statementData.transactions.forEach((tx) => {
      rows.push([
        tx.voucherDate,
        tx.voucherNo,
        tx.voucherType,
        tx.referenceNo,
        tx.particular,
        tx.debit || "",
        tx.credit || "",
        tx.balance,
        tx.balanceType,
      ]);
    });

    rows.push([
      "Total",
      "",
      "",
      "",
      "",
      statementData.summary.totalDebit,
      statementData.summary.totalCredit,
      statementData.closingBalance.amount,
      statementData.closingBalance.type,
    ]);

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");
    XLSX.writeFile(wb, `Ledger_${account.headName.replace(/[^a-zA-Z0-9]/g, "_")}.xlsx`);
    showToast.success("Exported Ledger statement to Excel");
  };

  return (
    <div className="ledger_page_wrapper">
      {/* Header Bar */}
      <div className="ledger_header_bar">
        <div className="ledger_breadcrumb">
          <span className="bc_prefix">Reports /</span> Statement of Account & Ledger
        </div>
        <div className="ledger_header_actions">
          <button
            type="button"
            className="vch_btn_export"
            onClick={handleExportExcel}
            disabled={!statementData?.transactions?.length}
          >
            <i className="fa-solid fa-file-excel"></i> Export Excel
          </button>
          <button
            type="button"
            className="vch_btn_export"
            onClick={() => window.print()}
            style={{ color: "#334155", borderColor: "#cbd5e1" }}
          >
            <i className="fa-solid fa-print"></i> Print
          </button>
        </div>
      </div>

      {/* Filter Form Card */}
      <div className="ledger_filter_card">
        <form
          className="ledger_filter_form"
          onSubmit={(e) => {
            e.preventDefault();
            fetchStatement();
          }}
        >
          <div className="ledger_filter_group">
            <label className="ledger_filter_label">Select Account:</label>
            <select
              className="ledger_select_account"
              value={selectedHeadCode}
              onChange={(e) => setSelectedHeadCode(e.target.value)}
            >
              {accounts.map((acc) => (
                <option key={acc.HEAD_CODE} value={acc.HEAD_CODE}>
                  {acc.HEADName} ({acc.GroupName})
                </option>
              ))}
            </select>
          </div>

          <div className="ledger_filter_group">
            <label className="ledger_filter_label">From:</label>
            <input
              type="date"
              className="ledger_date_input"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="ledger_filter_group">
            <label className="ledger_filter_label">To:</label>
            <input
              type="date"
              className="ledger_date_input"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          <button type="submit" className="ledger_btn_view">
            <i className="fa-solid fa-eye"></i> View Statement
          </button>
        </form>
      </div>

      {/* Account Info & Balance Card */}
      {statementData && (
        <div className="ledger_info_card">
          <div className="ledger_account_main">
            <h3>
              {statementData.account.headName}
              <span className="ledger_group_tag">{statementData.account.groupName}</span>
            </h3>
            <div className="ledger_account_meta">
              {statementData.account.gstin !== "-" && (
                <span><strong>GSTIN:</strong> {statementData.account.gstin}</span>
              )}
              {statementData.account.pan !== "-" && (
                <span><strong>PAN:</strong> {statementData.account.pan}</span>
              )}
              {statementData.account.address !== "-" && (
                <span><strong>Address:</strong> {statementData.account.address}</span>
              )}
            </div>
          </div>

          <div className="ledger_balance_boxes">
            <div className="ledger_bal_box opening">
              <div className="ledger_bal_title">Opening Balance</div>
              <div className={`ledger_bal_amount ${statementData.openingBalance.type}`}>
                ₹{formatINR(statementData.openingBalance.amount)}{" "}
                <small style={{ fontSize: "11px" }}>{statementData.openingBalance.type}</small>
              </div>
            </div>

            <div className="ledger_bal_box closing">
              <div className="ledger_bal_title">Closing Balance</div>
              <div className={`ledger_bal_amount ${statementData.closingBalance.type}`}>
                ₹{formatINR(statementData.closingBalance.amount)}{" "}
                <small style={{ fontSize: "11px" }}>{statementData.closingBalance.type}</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transactions Table Card */}
      <div className="ledger_table_card">
        {isLoading ? (
          <MasterTableSkeleton columns={6} rows={6} hasToolbar={false} />
        ) : !statementData || statementData.transactions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
            <i className="fa-regular fa-folder-open fa-2x mb-2"></i>
            <h4>No transactions found</h4>
            <p>No ledger voucher entries for this period.</p>
          </div>
        ) : (
          <table className="ledger_table">
            <thead>
              <tr>
                <th style={{ width: "95px" }}>Date</th>
                <th style={{ width: "110px" }}>Voucher No</th>
                <th style={{ width: "90px" }}>Type</th>
                <th style={{ width: "110px" }}>Reference #</th>
                <th>Particulars / Narration</th>
                <th style={{ width: "120px", textAlign: "right" }}>Debit (Dr ₹)</th>
                <th style={{ width: "120px", textAlign: "right" }}>Credit (Cr ₹)</th>
                <th style={{ width: "130px", textAlign: "right" }}>Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {/* Opening balance row */}
              <tr style={{ background: "#f8fafc", fontStyle: "italic" }}>
                <td colSpan="5">
                  <strong>By Opening Balance</strong>
                </td>
                <td style={{ textAlign: "right" }}>
                  {statementData.openingBalance.type === "Dr"
                    ? `₹${formatINR(statementData.openingBalance.amount)}`
                    : "-"}
                </td>
                <td style={{ textAlign: "right" }}>
                  {statementData.openingBalance.type === "Cr"
                    ? `₹${formatINR(statementData.openingBalance.amount)}`
                    : "-"}
                </td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>
                  ₹{formatINR(statementData.openingBalance.amount)}{" "}
                  <span className={`bal_badge_${statementData.openingBalance.type.toLowerCase()}`}>
                    {statementData.openingBalance.type}
                  </span>
                </td>
              </tr>

              {/* Transactions */}
              {statementData.transactions.map((tx) => (
                <tr key={tx.detailId}>
                  <td>{tx.voucherDate}</td>
                  <td style={{ fontWeight: 600 }}>{tx.voucherNo}</td>
                  <td>
                    <span
                      style={{
                        padding: "2px 7px",
                        borderRadius: "10px",
                        fontSize: "10.5px",
                        fontWeight: 700,
                        background: tx.voucherType === "Sales" ? "#dcfce7" : "#ede9fe",
                        color: tx.voucherType === "Sales" ? "#15803d" : "#7c3aed",
                      }}
                    >
                      {tx.voucherType}
                    </span>
                  </td>
                  <td>{tx.referenceNo}</td>
                  <td>{tx.particular}</td>
                  <td style={{ textAlign: "right", fontWeight: tx.debit > 0 ? 700 : 400 }}>
                    {tx.debit > 0 ? `₹${formatINR(tx.debit)}` : "-"}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: tx.credit > 0 ? 700 : 400 }}>
                    {tx.credit > 0 ? `₹${formatINR(tx.credit)}` : "-"}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>
                    ₹{formatINR(tx.balance)}{" "}
                    <span className={`bal_badge_${tx.balanceType.toLowerCase()}`}>
                      {tx.balanceType}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" style={{ textAlign: "right" }}>
                  Total Current Period Transactions:
                </td>
                <td style={{ textAlign: "right", color: "#064e48" }}>
                  ₹{formatINR(statementData.summary.totalDebit)}
                </td>
                <td style={{ textAlign: "right", color: "#064e48" }}>
                  ₹{formatINR(statementData.summary.totalCredit)}
                </td>
                <td style={{ textAlign: "right", color: "#064e48" }}>
                  ₹{formatINR(statementData.closingBalance.amount)}{" "}
                  <span className={`bal_badge_${statementData.closingBalance.type.toLowerCase()}`}>
                    {statementData.closingBalance.type}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}
